import { useEffect } from "react";
import { business } from "@/lib/business";

interface Props {
  /** Under 60 characters, keyword-first, ending with the brand name. */
  title: string;
  /** 145–155 characters. */
  description: string;
  /** Route path, e.g. "/services/masonry". Used for the canonical URL. */
  path: string;
  /** Absolute or root-relative image for og:image. Defaults to the brand OG card. */
  image?: string;
  type?: "website" | "article";
  /** Extra JSON-LD graph nodes for this route. */
  schema?: Record<string, unknown>[];
  noindex?: boolean;
}

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/** Organisation + local business node included on every page. */
const localBusinessSchema = () => ({
  "@type": "LandscapingBusiness",
  "@id": `${business.domain}/#organization`,
  name: business.legalName,
  alternateName: business.name,
  url: business.domain,
  logo: `${business.domain}/logo.png`,
  image: `${business.domain}/og-image.png`,
  telephone: business.phone,
  email: business.email,
  founder: business.owner,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: business.address.street,
    addressLocality: business.address.city,
    addressRegion: business.address.stateShort,
    postalCode: business.address.zip,
    addressCountry: business.address.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: business.address.lat,
    longitude: business.address.lng,
  },
  openingHours: business.hours.schema,
  areaServed: {
    "@type": "GeoCircle",
    geoMidpoint: {
      "@type": "GeoCoordinates",
      latitude: business.address.lat,
      longitude: business.address.lng,
    },
    geoRadius: business.serviceRadiusMiles * 1609,
  },
  sameAs: [business.social.facebook],
});

const SEO = ({ title, description, path, image, type = "website", schema = [], noindex }: Props) => {
  useEffect(() => {
    const canonical = `${business.domain}${path === "/" ? "/" : path.replace(/\/$/, "")}`;
    /*
     * Pages render WebP, but several link scrapers still do not decode it, so
     * social cards point at the JPEG that ships alongside every WebP.
     */
    const shareable = image?.replace(/\.webp$/i, ".jpg");
    const ogImage = shareable
      ? shareable.startsWith("http")
        ? shareable
        : `${business.domain}${shareable}`
      : `${business.domain}/og-image.png`;

    document.title = title;
    document.documentElement.lang = "en";

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
    );
    upsertLink("canonical", canonical);

    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", business.name);
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", "en_US");

    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);

    upsertMeta(
      'meta[name="geo.region"]',
      "name",
      "geo.region",
      `US-${business.address.stateShort}`
    );
    upsertMeta('meta[name="geo.placename"]', "name", "geo.placename", business.address.city);

    const graph = [localBusinessSchema(), ...schema];
    let ld = document.head.querySelector<HTMLScriptElement>("#nimar-jsonld");
    if (!ld) {
      ld = document.createElement("script");
      ld.id = "nimar-jsonld";
      ld.type = "application/ld+json";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, noindex, JSON.stringify(schema)]);

  return null;
};

export default SEO;
